/**
 * Caregiver Name on clinical forms:
 * - Admin/OA (create or edit, when canEdit) → dropdown of Active assigned staff
 * - Clinician (RN/LVN/…) → their own name (static, no dropdown)
 */

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { getPatientAssignments } from "../../../../services/patientService";
import { USER_ROLES } from "../../../../constants";

/** Matches HeaderCell dense value line — no native select chrome / focus ring. */
const caregiverSelectClass =
  "w-full h-4 leading-4 text-[10px] font-bold text-slate-900 bg-transparent border-0 p-0 pr-3.5 outline-none appearance-none cursor-pointer truncate disabled:text-slate-500 disabled:cursor-default";

/** Roles allowed as filledBy for a form type (mirrors backend caregiverRolesForFormType). */
export const caregiverRolesForFormType = (formType) => {
  const prefix = String(formType || "")
    .split("_")[0]
    .toLowerCase();
  switch (prefix) {
    case "rn":
      return ["RN", "LVN"];
    case "pt":
      return ["PT", "PTA"];
    case "ot":
      return ["OT", "OTA"];
    case "st":
      return ["ST"];
    case "msw":
      return ["MSW"];
    case "chha":
      return ["HHA"];
    case "daily":
      return [
        "RN",
        "LVN",
        "PT",
        "PTA",
        "OT",
        "OTA",
        "ST",
        "MSW",
        "HHA",
        "CNA",
        "CG",
      ];
    default:
      return [
        "RN",
        "LVN",
        "PT",
        "PTA",
        "OT",
        "OTA",
        "ST",
        "MSW",
        "HHA",
        "CNA",
        "CG",
      ];
  }
};

export const isClinicalFormManagerRole = (role) =>
  role === USER_ROLES.ADMIN || role === USER_ROLES.OA;

/** Draft identity: managers leave caregiver blank until they pick; clinicians stamp themselves. */
export const draftCaregiverIdentity = (currentUser, role) => {
  if (isClinicalFormManagerRole(role)) {
    return { filledByName: "", filledByRole: "" };
  }
  return {
    filledByName: [currentUser?.firstName, currentUser?.lastName]
      .filter(Boolean)
      .join(" "),
    filledByRole: role || "",
  };
};

export const formatCaregiverOptionLabel = (staff) => {
  const name = [staff?.firstName, staff?.lastName].filter(Boolean).join(" ");
  const staffRole = staff?.role || "";
  if (name && staffRole) return `${name} (${staffRole})`;
  return name || staffRole || "Staff";
};

export const filterAssignedCaregivers = (assignedStaff, formType) => {
  const allowed = new Set(caregiverRolesForFormType(formType));
  return (assignedStaff || []).filter((s) => {
    const status = String(s.status || "Active");
    if (status && status !== "Active") return false;
    return allowed.has(String(s.role || "").toUpperCase());
  });
};

export const getAssignmentEmployeeId = (staff) =>
  staff?.employeeId ?? staff?.userId ?? staff?.id ?? null;

/**
 * Dense select matching HeaderCell / header date picker height (one row).
 */
export const CaregiverSelectField = ({
  value,
  onChange,
  options,
  disabled = false,
  loading = false,
}) => (
  <div className="min-w-0">
    <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
      Caregiver Name
    </div>
    <div className="relative mt-0.5 h-4 flex items-center min-w-0">
      <select
        name="caregiverUserId"
        className={caregiverSelectClass}
        value={value ? String(value) : ""}
        disabled={disabled || loading}
        onChange={(event) => onChange(event.target.value || "")}
      >
        <option value="">
          {loading
            ? "Loading…"
            : options.length
              ? "Select caregiver…"
              : "No assigned caregivers"}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
      <span
        className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-slate-400"
        aria-hidden
      >
        <svg width="10" height="10" viewBox="0 0 20 20" fill="none">
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  </div>
);

CaregiverSelectField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
  ).isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
};

/**
 * @returns {{
 *   caregiverSlot: React.ReactNode | null,
 *   filledByUserId: number | null,
 *   assertCaregiverForCreate: () => string | null,
 * }}
 */
export function useFormCaregiverPicker({
  patientId,
  formType,
  role,
  canEdit,
  /** Prefer calendar staff on brand-new drafts when no filledBy yet. */
  preferredStaffId = null,
  /** Current form filledBy (edit) — seeds the dropdown. */
  currentFilledByUserId = null,
}) {
  const canPick =
    Boolean(canEdit) &&
    isClinicalFormManagerRole(role) &&
    Boolean(formType);

  const [caregiverUserId, setCaregiverUserId] = useState(() =>
    currentFilledByUserId ? String(currentFilledByUserId) : "",
  );
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Keep selection in sync when the loaded form's filledBy arrives / changes.
  useEffect(() => {
    if (!canPick) return;
    if (currentFilledByUserId != null && currentFilledByUserId !== "") {
      setCaregiverUserId(String(currentFilledByUserId));
    }
  }, [canPick, currentFilledByUserId]);

  useEffect(() => {
    if (!canPick || !patientId) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getPatientAssignments(patientId)
      .then((res) => {
        if (cancelled) return;
        const assigned =
          res?.result?.assignedStaff ||
          res?.assignedStaff ||
          res?.data?.assignedStaff ||
          [];
        const filtered = filterAssignedCaregivers(assigned, formType);
        const next = filtered
          .map((s) => {
            const id = getAssignmentEmployeeId(s);
            if (id == null) return null;
            return {
              value: Number(id),
              label: formatCaregiverOptionLabel(s),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));
        setOptions(next);

        setCaregiverUserId((prev) => {
          if (prev && next.some((o) => String(o.value) === String(prev))) {
            return prev;
          }
          const current = currentFilledByUserId
            ? Number(currentFilledByUserId)
            : null;
          if (current && next.some((o) => o.value === current)) {
            return String(current);
          }
          const preferred = preferredStaffId ? Number(preferredStaffId) : null;
          if (preferred && next.some((o) => o.value === preferred)) {
            return String(preferred);
          }
          if (!prev && next.length === 1) {
            return String(next[0].value);
          }
          return prev || "";
        });
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canPick, patientId, formType, preferredStaffId, currentFilledByUserId]);

  const caregiverSlot = useMemo(() => {
    if (!canPick) return null;
    return (
      <CaregiverSelectField
        value={caregiverUserId}
        onChange={setCaregiverUserId}
        options={options}
        loading={loading}
        disabled={!canEdit}
      />
    );
  }, [canPick, caregiverUserId, options, loading, canEdit]);

  const filledByUserId = caregiverUserId ? Number(caregiverUserId) : null;

  const assertCaregiverForCreate = () => {
    if (!canPick) return null;
    if (!filledByUserId) {
      return options.length
        ? "Select a caregiver before saving"
        : "Assign matching clinical staff to this patient before saving the form";
    }
    return null;
  };

  return {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  };
}
